import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Collapse,
  Container,
  Grid,
  List,
  ListItem,
  ListItemText,
  Theme,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { AutocompleteTextField, FilterItem, JobPosition } from 'components/structures/careers';
import { Pagination } from '@material-ui/lab';
import { NewsPaginationItem } from 'components/structures/news';
import { CAREERS_PAGE_SIZE } from 'config/cms/endpoints';
import services from 'services';
import { Career, CareersCategory, CareersCities, CareersFilter, CareersTypes } from 'services/careers/types';
import { categoriesToItems, citiesToItems, typesToItems } from 'utils/careers';
import { FilterIcon } from 'svgs/careers';
import { getPageCount } from 'utils/common/list';
import { ParsedUrlQuery } from 'querystring';
import { useRouter } from 'next/router';
import { objectToQueryString } from 'utils/common/http';
import EmptyCategory from './EmptyCatogory';

interface JobPageProps {
  categories: CareersCategory[];
  cities: CareersCities[];
  types: CareersTypes[];
  titles: string[];
  title: string;
  careers: Career[];
  total: number;
  page: number;
}

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    root: {
      backgroundColor: theme.palette.background.default,
    },
    fixedBlock: {
      backgroundColor: theme.palette.common.white,
      padding: theme.spacing(3, 4),
      top: 118,
    },
    title: {
      fontWeight: 500,
      color: theme.palette.primary.dark,
      marginBottom: theme.spacing(3),
    },
    mobileBlock: {
      backgroundColor: theme.palette.common.white,
      padding: theme.spacing(0, 3),
    },
    listItem: {
      '&:focus': {
        color: 'transparent',
      },
    },
    container: {
      position: 'relative',
    },
    mobileTitle: {
      color: theme.palette.text.secondary,
      '& span': {
        fontSize: 18,
        fontWeight: 500,
      },
    },
    icon: {
      position: 'absolute',
      top: 8,
      left: -30,
    },
  });
});

let isFirstLoading = true;

export const createRequest = (query: ParsedUrlQuery) => {
  const filter = ['category', 'location', 'type'].reduce(
    (acc, id) => ({
      ...acc,
      [id]: query[id] ? String(query[id]).split(',') : [],
    }),
    {},
  );
  return {
    page: query.page ? Number(query.page) : 1,
    filter,
    title: query.title || '',
  };
};

const filterCategories = (initialCategories: CareersCategory[], newCareers: Career[]) =>
  initialCategories.filter(
    (category) => !category.u1_careers.length || newCareers.some((career) => career.categoryIds.includes(category.id)),
  );

const filterCities = (initialCities: CareersCities[], newCareers: Career[]) =>
  initialCities.filter((city) => !city.u1_careers.length || newCareers.some((career) => career.locationIds.includes(city.id)));

const filterTypes = (initialTypes: CareersTypes[], newCareers: Career[]) =>
  initialTypes.filter(
    (jobType) => !jobType.u1_careers.length || newCareers.some((career) => career.typeIds.includes(jobType.id)),
  );

const JobPage: FC<JobPageProps> = (props) => {
  const router = useRouter();
  const {
    page: initialPage,
    careers: initialCareers,
    total: initialTotal,
    categories: initialCategories,
    cities: initialCities,
    types: initialTypes,
    titles,
    title: initialTitle,
  } = props;
  const classes = useStyles();
  const isMedium = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const [careers, setCareers] = useState<Array<Career>>(initialCareers);
  const categories = useMemo<CareersCategory[]>(() => filterCategories(initialCategories, careers), [careers]);
  const cities = useMemo<CareersCities[]>(() => filterCities(initialCities, careers), [careers]);
  const types = useMemo<CareersTypes[]>(() => filterTypes(initialTypes, careers), [careers]);
  const [open, setOpen] = React.useState(false);
  const [title, setTitle] = React.useState<string>(initialTitle);

  const pushQuery = (query: ParsedUrlQuery) => {
    router.push('?' + objectToQueryString(query), undefined, { shallow: true }).then();
  };

  const onChangeTitle = (e, value) => {
    pushQuery({ ...router.query, title: value || '', page: String(1), location: '', type: '', category: '' });
  };

  useEffect(() => {
    if (isFirstLoading) {
      isFirstLoading = false;
      return;
    }
    (async () => {
      const { page, filter, title } = createRequest(router.query);
      const { data: newCareers, total: newTotal } = await careersService.getAllCareers(page, filter, String(title));
      setPage(page);
      setTitle(String(title));
      setTotal(newTotal);
      setChecks(filter);
      setCareers(newCareers);
      scrollToTop();
    })();
  }, [router.query]);

  const handleClick = () => {
    setOpen(!open);
  };

  const [page, setPage] = useState<number>(initialPage);
  const [total, setTotal] = useState<number>(initialTotal);
  const totalPages = getPageCount(total, CAREERS_PAGE_SIZE);
  const { careersService } = services;

  const scrollToTop = () => {
    window.scrollTo({
      top: 250,
      behavior: 'smooth',
    });
  };

  const filterList = [
    {
      filterName: 'Job Category',
      filterId: 'category',
      listItems: categoriesToItems(categories),
    },
    {
      filterName: 'Locations',
      filterId: 'location',
      listItems: citiesToItems(cities),
    },
    {
      filterName: 'Job Type',
      filterId: 'type',
      listItems: typesToItems(types),
    },
  ];

  const changePage = async (page: number) => {
    pushQuery({ ...router.query, page: String(page) });
  };

  const [checks, setChecks] = useState<CareersFilter>(() => {
    const { filter } = createRequest(router.query);
    return filter;
  });

  const onChangeFilter = (filterId, newChecks: string[]) => {
    pushQuery({ ...router.query, [filterId]: newChecks.join(','), page: String(1), title: '' });
  };

  const careersTotal = careers.length;

  return (
    <Box pt={isMedium ? 11 : 10} pb={isMedium ? 30 : 19} className={classes.root}>
      <Container fixed>
        <Grid container direction="row" justify="space-between" spacing={5}>
          <Grid xs={12} md={4} item data-aos="fade-up">
            {isMedium ? (
              <Box className={classes.fixedBlock} position={isMedium ? 'sticky' : 'inherit'}>
                <Typography variant="h6" className={classes.title} align="center">
                  FILTERS
                </Typography>
                {filterList.map((item) => (
                  <FilterItem
                    {...item}
                    key={item.filterId}
                    onChange={onChangeFilter}
                    checks={checks[item.filterId]}
                    careersTotal={careersTotal}
                  />
                ))}
              </Box>
            ) : (
              <Box className={classes.mobileBlock} position={isMedium ? 'sticky' : 'inherit'}>
                <List component="nav" aria-labelledby="nested-list-subheader">
                  <ListItem button onClick={handleClick} className={classes.listItem}>
                    <Grid direction="row" justify="center" container>
                      <Grid item className={classes.container}>
                        <FilterIcon className={classes.icon} />
                        <ListItemText primary={open ? 'HIDE FILTERS' : 'SHOW FILTERS'} className={classes.mobileTitle} />
                      </Grid>
                    </Grid>
                  </ListItem>
                  <Collapse in={open} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {filterList.map((item) => (
                        <FilterItem
                          {...item}
                          key={item.filterId}
                          onChange={onChangeFilter}
                          checks={checks[item.filterId]}
                          careersTotal={careersTotal}
                        />
                      ))}
                      <Box mb={3} />
                    </List>
                  </Collapse>
                </List>
              </Box>
            )}
          </Grid>
          {careers.length > 0 ? (
            <Grid xs={12} md={8} item data-aos="fade-up" data-aos-delay="300">
              <AutocompleteTextField value={title} onChange={onChangeTitle} options={titles} />
              {careers.map((item) => (
                <JobPosition
                  href={`/careers/jobs/${item.link}`}
                  description={item.description}
                  jobTitle={item.title}
                  place={item.location.name}
                  shortNamePlace={item.location.state.short_name}
                  publicData={item.publish_at}
                  workExperience={
                    item.years_in_industry > 1 ? `${item.years_in_industry} years` : `${item.years_in_industry} year`
                  }
                  key={item.id}
                  jobType={item.jobtype.name}
                />
              ))}
              {totalPages > 1 && (
                <Box mt={9.6}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, page) => changePage(page)}
                    shape="rounded"
                    boundaryCount={1}
                    siblingCount={0}
                    renderItem={(item) => <NewsPaginationItem {...item} />}
                  />
                </Box>
              )}
            </Grid>
          ) : (
            <Grid xs={12} md={8} item data-aos="fade-up" data-aos-delay="300">
              <AutocompleteTextField value={title} onChange={onChangeTitle} options={titles} />
              <EmptyCategory />
              {totalPages > 1 && (
                <Box mt={9.6}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, page) => changePage(page)}
                    shape="rounded"
                    boundaryCount={1}
                    siblingCount={0}
                    renderItem={(item) => <NewsPaginationItem {...item} />}
                  />
                </Box>
              )}
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default JobPage;
